// src/components/LandingPage.jsx
// Marketing surface. Hero + feature grid + AI demo + CTA.

import React, { useState as useState_LP, useEffect as useEffect_LP } from "react";
import * as PPIcons from "./_icons";
import { Button, CategoryChip, Chip, LiveDot, SeverityChip } from "./_primitives";
import PinchIcon from "./PinchIcon";

const INDUSTRIES = ["Aviation", "Logistics", "Warehousing", "Manufacturing", "Facilities", "Hospitality", "Events"];

const HERO_EXAMPLES = {
  Aviation: { sev: "critical", cat: "Ramp Ops", desc: "Cargo hold scuff plates on Aircraft 738 are snagging bags. Slowing offload 5–10 min per turn.", votes: 12 },
  Logistics: { sev: "moderate", cat: "Comm", desc: "Radio interference in Zone D — drivers missing gate instructions, yard backing up.", votes: 9 },
  Warehousing: { sev: "moderate", cat: "Efficiency", desc: "Inaccurate bin labeling in Row 40-45. Picking staff searching for non-standard inventory.", votes: 11 },
  Manufacturing: { sev: "moderate", cat: "Quality", desc: "Calibration drift on weighing scale #4. Variances requiring manual re-checks each batch.", votes: 15 },
  Facilities: { sev: "moderate", cat: "Energy", desc: "South Lobby door closer lost tension. Door propped ~6\" open, bleeding HVAC.", votes: 10 },
  Hospitality: { sev: "moderate", cat: "Guest Exp", desc: "Hot water timing in West Wing — 3–5 min reach time. Guests complaining at checkout.", votes: 8 },
  Events: { sev: "critical", cat: "Signage", desc: "Unclear signage at secondary badge pickup. 30% of attendees in the wrong line.", votes: 18 },
};

function LandingPage({ onSignIn, onRegister }) {
  const [active, setActive] = useState_LP(0);
  useEffect_LP(() => {
    const t = setInterval(() => setActive(i => (i + 1) % INDUSTRIES.length), 3500);
    return () => clearInterval(t);
  }, []);
  const industry = INDUSTRIES[active];
  const example = HERO_EXAMPLES[industry];

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {/* Nav */}
      <header style={{
        position: "sticky", top: 0, zIndex: 20,
        background: "var(--bg)80", backdropFilter: "blur(8px)",
        borderBottom: "1px solid var(--border)",
        padding: "14px 28px",
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div className="pp-brand">
          <span className="pp-brand-mark"><PinchIcon size={16} /></span>
          Pinch Point
        </div>
        <div style={{ flex: 1 }} />
        <nav style={{ display: "flex", gap: 18, fontSize: 13, color: "var(--text-dim)", fontWeight: 500 }}>
          <a href="#features">Features</a>
          <a href="#ai">AI</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <Button variant="ghost" size="sm" onClick={onSignIn}>Sign in</Button>
        <Button variant="primary" size="sm" onClick={onRegister}>Get started →</Button>
      </header>

      {/* Hero */}
      <section style={{
        padding: "60px 28px 40px",
        display: "grid", gridTemplateColumns: "1.1fr 1fr",
        gap: 48, alignItems: "center", maxWidth: 1200, margin: "0 auto",
      }}>
        <div>
          <div style={{ marginBottom: 14 }}>
            <Chip tone="ai" dot={false}>
              <PPIcons.IconSparkle style={{ width: 11, height: 11 }} />
              AI-assisted operations feedback
            </Chip>
          </div>
          <h1 style={{
            fontSize: 56, fontWeight: 700, color: "var(--text-strong)",
            letterSpacing: "-0.025em", lineHeight: 1.05, marginBottom: 18,
          }}>
            The friction log<br />for high-tempo teams.
          </h1>
          <p style={{
            fontSize: 17, color: "var(--text-dim)", lineHeight: 1.5, maxWidth: 520,
            marginBottom: 24,
          }}>
            Frontline workers report the small things that slow them down. AI
            categorizes, clusters, and turns recurring patterns into actionable
            tasks — assigned to the right vendor, with context.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="primary" size="xl" onClick={onRegister}>Start free →</Button>
            <Button variant="secondary" size="xl">Book a demo</Button>
          </div>
          <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 16 }}>
            <LiveDot label="2,481 pinch points logged this week" />
          </div>
        </div>

        {/* Hero card carousel */}
        <div style={{ position: "relative", height: 380 }}>
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(circle at 70% 30%, var(--accent-soft), transparent 60%)",
            borderRadius: 32,
          }} />
          <div style={{
            position: "absolute", top: 40, left: 30, right: 30,
            background: "var(--bg-elevated)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)", padding: 18,
            boxShadow: "var(--shadow-lg)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span className="pp-brand-mark" style={{ width: 22, height: 22, borderRadius: 6 }}>
                <PinchIcon size={12} />
              </span>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{industry} board</div>
              <span style={{ marginLeft: "auto" }}><LiveDot label="LIVE" /></span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[example, HERO_EXAMPLES[INDUSTRIES[(active + 1) % INDUSTRIES.length]],
                HERO_EXAMPLES[INDUSTRIES[(active + 2) % INDUSTRIES.length]]].map((ex, i) => (
                <div key={i} className={`pp-card pp-card--railed pp-card--sev-${ex.sev === "critical" ? "crit" : ex.sev === "moderate" ? "mod" : "low"}`}
                  style={{ animation: i === 0 ? "pp-slide-in .3s" : "none" }}>
                  <div className="pp-card-top">
                    <CategoryChip category={ex.cat} />
                    <SeverityChip severity={ex.sev} />
                  </div>
                  <p className="pp-card-desc" style={{ fontSize: 12 }}>{ex.desc}</p>
                  <div className="pp-card-foot">
                    <span className="pp-author">Operator</span>
                    <span className="pp-sep">·</span>
                    <span>just now</span>
                    <span className="pp-card-upvote">
                      <PPIcons.IconArrowUp /> {ex.votes}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry pills */}
      <section style={{
        padding: "8px 28px 60px", maxWidth: 1200, margin: "0 auto",
        display: "flex", alignItems: "center", gap: 14, justifyContent: "center", flexWrap: "wrap",
      }}>
        <span style={{
          fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)",
          textTransform: "uppercase", letterSpacing: 0.06,
        }}>
          Built for
        </span>
        {INDUSTRIES.map((i, idx) => (
          <button key={i} onClick={() => setActive(idx)}
            style={{
              padding: "6px 12px", borderRadius: 999, border: "1px solid",
              borderColor: active === idx ? "var(--accent)" : "var(--border)",
              background: active === idx ? "var(--accent-soft)" : "transparent",
              color: active === idx ? "var(--accent)" : "var(--text-dim)",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "all .12s",
            }}>{i}</button>
        ))}
      </section>

      {/* Features grid */}
      <section id="features" style={{
        padding: "60px 28px", maxWidth: 1200, margin: "0 auto",
      }}>
        <h2 style={{
          fontSize: 36, fontWeight: 700, color: "var(--text-strong)",
          letterSpacing: "-0.02em", textAlign: "center", marginBottom: 12,
        }}>
          Built for the people who actually touch the operation.
        </h2>
        <p style={{
          fontSize: 16, color: "var(--text-dim)", textAlign: "center",
          maxWidth: 560, margin: "0 auto 40px",
        }}>
          One log. AI as the editor. Tasks routed to the right vendor.
          Patterns surfaced before they become a fire drill.
        </p>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {[
            { icon: <PPIcons.IconQr />, title: "Frictionless capture", body: "QR + anonymous link. Submit in 30 seconds — no login required." },
            { icon: <PPIcons.IconList />, title: "Triage at a glance", body: "Severity-grouped Live Logs. Sort by upvotes. Status pipeline per card." },
            { icon: <PPIcons.IconSparkle />, title: "Smart task generation", body: "AI clusters posts and drafts tasks. Matches to your vendors automatically." },
            { icon: <PPIcons.IconShield />, title: "Guardrail governance", body: "Your guidelines filter every post. Stays constructive, never a complaint box." },
            { icon: <PPIcons.IconChart />, title: "Patterns over noise", body: "Systemic alerts spot recurring issues across days, weeks, months." },
            { icon: <PPIcons.IconBell />, title: "Push assignments", body: "FCM push notifications. Accept/deny without opening the app." },
          ].map((f, i) => (
            <div key={i} style={{
              padding: 20, border: "1px solid var(--border)",
              borderRadius: "var(--radius)", background: "var(--bg-elevated)",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 9, background: "var(--accent-soft)",
                color: "var(--accent)", display: "grid", placeItems: "center",
                marginBottom: 12,
              }}>{f.icon}</div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text-strong)", marginBottom: 6 }}>{f.title}</h3>
              <p style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: "60px 28px 80px", maxWidth: 900, margin: "0 auto",
        textAlign: "center",
      }}>
        <div style={{
          background: "var(--text-strong)", color: "var(--bg-elevated)",
          borderRadius: "var(--radius-lg)", padding: "48px 32px",
        }}>
          <h2 style={{
            fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em",
            marginBottom: 12,
          }}>Start logging friction today.</h2>
          <p style={{ fontSize: 15, opacity: 0.7, marginBottom: 24 }}>
            Free for up to 25 members. No credit card.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
            <Button variant="primary" size="xl" onClick={onRegister}>Create your org →</Button>
            <Button variant="ghost" size="xl"
              style={{ background: "transparent", color: "currentColor", borderColor: "currentColor" }}
              onClick={onSignIn}>
              I already have an account
            </Button>
          </div>
        </div>
      </section>

      <footer style={{
        padding: "24px 28px", borderTop: "1px solid var(--border)",
        textAlign: "center", fontSize: 12, color: "var(--text-faint)",
      }}>
        © 2026 Darn Good Home Inspections LLC
      </footer>
    </div>
  );
}

export default LandingPage;
